import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useComputedColorScheme } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store/userStore';
import {
  useChooseCompany,
  useLogin,
  useRegister,
  useResendCode,
  useVerifyCode,
  type CompanyChoice,
} from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { Logo } from '../../components/app/Logo';
import { ApiError, api, getErrorMessage } from '../../utils/api';
import { useTenant } from '../../contexts/TenantContext';

// Build the message shown to the user from a mutation error. Network-level
// failures (no response: server unreachable, CORS, dropped connection) surface
// as an ApiError with status 0 or a raw "Failed to fetch"/"Network Error" — for
// those we show a friendly, localized message instead of the raw browser text.
function toErrorText(err: unknown, networkMsg: string, fallbackMsg: string): string {
  const isNetwork =
    (err instanceof ApiError && err.status === 0) ||
    (err instanceof Error &&
      (err.message === 'Failed to fetch' || err.message === 'Network Error'));
  return isNetwork ? networkMsg : getErrorMessage(err, fallbackMsg);
}

// ── theme tokens (scoped to .auth-root, mirrors the design 1:1) ──────────────
const SCOPED_STYLES = `
.auth-root {
  --bg: #F8F9FA;
  --surface: #FFFFFF;
  --surface-2: #F1F3F5;
  --line: #E9ECEF;
  --line-strong: #DEE2E6;
  --ink: #212529;
  --ink-2: #495057;
  --muted: #868E96;
  --soft: #ADB5BD;
  --accent: #12B886;
  --accent-soft: #E6FCF5;
  --accent-deep: #099268;
  --danger: #E03131;
  --danger-soft: #FFF5F5;
  --radius-sm: 6px;
  --radius: 8px;
  --radius-lg: 12px;
  --shadow: 0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03);
  --shadow-md: 0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04);
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
}
.auth-root.dark {
  --bg: #1A1B1E;
  --surface: #25262B;
  --surface-2: #2C2E33;
  --line: #373A40;
  --line-strong: #5C5F66;
  --ink: #C1C2C5;
  --ink-2: #A6A7AB;
  --muted: #909296;
  --soft: #5C5F66;
  --accent: #20C997;
  --accent-soft: #0C3B30;
  --accent-deep: #63E6BE;
  --danger: #FF6B6B;
  --danger-soft: #3B1F1F;
}
.auth-root .num { font-variant-numeric: tabular-nums; }
.auth-root h1, .auth-root h2, .auth-root h3 { margin: 0; font-weight: 600; color: var(--ink); }
.auth-root p { margin: 0; }
.auth-root button { font-family: inherit; }
.auth-root a { color: inherit; text-decoration: none; }
@keyframes auth-spin { to { transform: rotate(360deg); } }
.auth-root .demo-role {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--surface);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
}
.auth-root .demo-role:hover:not(:disabled),
.auth-root .demo-role:focus-visible { border-color: var(--accent); outline: none; }
.auth-root .demo-role:disabled { cursor: wait; opacity: 0.7; }

/* Метаболы на брендовой половине: пятна дрейфуют по своим орбитам и сливаются
   в одно, когда сходятся. Ровно то, чем занят продукт: отдельные компетенции
   складываются в грейд. Живут ниже точечной сетки и всего контента. */
.auth-root .goo {
  position: absolute;
  inset: 0;
  z-index: 0;
  opacity: 0.3;
  pointer-events: none;
  filter: url(#auth-goo);
}
.auth-root.dark .goo { opacity: 0.18; }
.auth-root .goo span {
  position: absolute;
  display: block;
  border-radius: 50%;
  background: #fff;
  will-change: transform;
}
.auth-root .goo .b1 { width: 300px; height: 300px; left: 4%;  top: 14%; animation: auth-drift-1 38s ease-in-out infinite; }
.auth-root .goo .b2 { width: 220px; height: 220px; left: 28%; top: 46%; animation: auth-drift-2 46s ease-in-out infinite; }
.auth-root .goo .b3 { width: 260px; height: 260px; left: 52%; top: 8%;  animation: auth-drift-3 52s ease-in-out infinite; }
@keyframes auth-drift-1 { 50% { transform: translate(140px, 150px) scale(1.12); } }
@keyframes auth-drift-2 { 50% { transform: translate(-90px, -120px) scale(0.9); } }
@keyframes auth-drift-3 { 50% { transform: translate(-120px, 190px) scale(1.08); } }
@media (prefers-reduced-motion: reduce) {
  .auth-root .goo span { animation: none; }
}
@media (max-width: 900px) {
  .auth-root .brand-panel { display: none !important; }
  .auth-root .form-panel { min-width: 0 !important; }
}
`;

// ── icons ────────────────────────────────────────────────────────────────────
const strokeProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
const MailIcon = () => (
  <svg {...strokeProps}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);
const UserIcon = () => (
  <svg {...strokeProps}>
    <circle cx="12" cy="9" r="4" />
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);
const ArrowIcon = () => (
  <svg {...strokeProps}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);
const BackIcon = () => (
  <svg {...strokeProps}>
    <path d="M19 12H5" />
    <path d="M11 6l-6 6 6 6" />
  </svg>
);
const AlertIcon = () => (
  <svg {...strokeProps}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16v.5" />
  </svg>
);
const Spinner: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    style={{ color, animation: 'auth-spin 0.8s linear infinite' }}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeDasharray="40 60"
      strokeLinecap="round"
    />
  </svg>
);

const FtMap = () => (
  <svg {...strokeProps}>
    <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
    <path d="M9 4v16M15 6v16" />
  </svg>
);
const FtClip = () => (
  <svg {...strokeProps}>
    <rect x="6" y="4" width="12" height="17" rx="2" />
    <path d="M9 4h6v3H9z" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);
const FtRotate = () => (
  <svg {...strokeProps}>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);
const FtBook = () => (
  <svg {...strokeProps}>
    <path d="M4 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4z" />
    <path d="M20 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8z" />
  </svg>
);

// ── brand panel decorations ──────────────────────────────────────────────────
function hexPointsArr(cx: number, cy: number, r: number): Array<[number, number]> {
  const arr: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    arr.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  return arr;
}
const hexPoints = (cx: number, cy: number, r: number) =>
  hexPointsArr(cx, cy, r)
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

const DotGrid: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      opacity: 0.22,
      pointerEvents: 'none',
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)',
      backgroundSize: '28px 28px',
    }}
  />
);

// Сам фильтр — склейка по альфа-каналу: размыть, затем вернуть резкую границу.
// Так пятна сливаются на прозрачном фоне, чего contrast() поверх градиента не умеет.
const GooBlobs: React.FC = () => (
  <>
    <svg width="0" height="0" aria-hidden style={{ position: 'absolute' }}>
      <filter id="auth-goo">
        <feGaussianBlur in="SourceGraphic" stdDeviation="24" result="blurred" />
        <feColorMatrix
          in="blurred"
          type="matrix"
          values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -12"
        />
      </filter>
    </svg>
    <div className="goo" aria-hidden>
      <span className="b1" />
      <span className="b2" />
      <span className="b3" />
    </div>
  </>
);

const BrandGrid: React.FC = () => (
  <svg
    width="640"
    height="640"
    viewBox="0 0 640 640"
    style={{
      position: 'absolute',
      right: -160,
      bottom: -120,
      opacity: 0.18,
      zIndex: 1,
      pointerEvents: 'none',
    }}
  >
    <defs>
      <radialGradient id="brandGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#fff" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0" />
      </radialGradient>
    </defs>
    {[120, 200, 280, 360, 440].map((r, i) => (
      <polygon
        key={r}
        points={hexPoints(320, 320, r)}
        fill="none"
        stroke="#fff"
        strokeOpacity={0.35 - i * 0.05}
        strokeWidth={i === 4 ? 2 : 1}
        strokeDasharray={i % 2 === 0 ? '4 6' : 'none'}
      />
    ))}
    <circle cx="320" cy="320" r="160" fill="url(#brandGlow)" />
    {hexPointsArr(320, 320, 280).map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={5} fill="#fff" fillOpacity={0.7} />
    ))}
  </svg>
);

const PreviewCard: React.FC = () => {
  const rows = [
    { name: 'Frontend Architecture', v: 4.6, pct: 92 },
    { name: 'System Design', v: 3.8, pct: 76 },
    { name: 'Менторство', v: 4.7, pct: 94 },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        right: -40,
        bottom: 110,
        width: 320,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 16,
        padding: 18,
        backdropFilter: 'blur(14px)',
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.45)',
        transform: 'rotate(-3deg)',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontWeight: 600,
          }}
        >
          Q2 2026 · промоут
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 700,
            color: '#63E6BE',
          }}
        >
          L4 → L5
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((r) => (
          <div key={r.name}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{r.name}</span>
              <span className="num" style={{ fontSize: 12, color: '#63E6BE', fontWeight: 700 }}>
                {r.v.toFixed(1)}
              </span>
            </div>
            <div
              style={{
                height: 4,
                background: 'rgba(255,255,255,0.14)',
                borderRadius: 999,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${r.pct}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #63E6BE, #20C997)',
                  borderRadius: 999,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          fontSize: 11,
          color: 'rgba(255,255,255,0.7)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: '#63E6BE' }} />
        Решение опубликовано · 5 минут назад
      </div>
    </div>
  );
};

const BrandPanel: React.FC<{ dark: boolean; name?: string }> = ({ dark, name }) => {
  const { t } = useTranslation();
  const features: Array<{ icon: React.ReactNode; title: string; desc: string }> = [
    {
      icon: <FtMap />,
      title: t('auth.brand.features.mapTitle'),
      desc: t('auth.brand.features.mapDesc'),
    },
    {
      icon: <FtClip />,
      title: t('auth.brand.features.reviewTitle'),
      desc: t('auth.brand.features.reviewDesc'),
    },
    {
      icon: <FtRotate />,
      title: t('auth.brand.features.feedbackTitle'),
      desc: t('auth.brand.features.feedbackDesc'),
    },
    {
      icon: <FtBook />,
      title: t('auth.brand.features.learningTitle'),
      desc: t('auth.brand.features.learningDesc'),
    },
  ];
  const stats = [
    { v: '180+', l: t('auth.brand.stats.competencies') },
    { v: '2', l: t('auth.brand.stats.reviewFormats') },
    { v: '1', l: t('auth.brand.stats.launchDays') },
  ];
  return (
    <div
      className="brand-panel"
      style={{
        flex: '1 1 50%',
        minWidth: 480,
        position: 'relative',
        background: dark
          ? 'radial-gradient(circle at 20% 0%, #1F3530 0%, transparent 60%), radial-gradient(circle at 100% 100%, #0A6B5A 0%, transparent 50%), linear-gradient(135deg, #0E1311 0%, #1A2421 100%)'
          : 'radial-gradient(circle at 20% 0%, #16B89B 0%, transparent 55%), radial-gradient(circle at 100% 100%, #0A6B5A 0%, transparent 60%), linear-gradient(135deg, #0A6B5A 0%, #099268 55%, #12B886 100%)',
        color: '#fff',
        padding: '48px 56px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    >
      <GooBlobs />
      <DotGrid />
      <BrandGrid />
      <PreviewCard />

      <header
        style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Logo variant="onDark" href="/" showSubtitle={false} name={name} />
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.22)',
            fontWeight: 500,
          }}
        >
          {t('auth.brand.version')}
        </span>
      </header>

      <div style={{ position: 'relative', zIndex: 3, maxWidth: 500 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontWeight: 600,
            opacity: 0.7,
            marginBottom: 18,
          }}
        >
          {t('auth.brand.tagline')}
        </div>
        <h1
          style={{
            fontSize: 38,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginBottom: 16,
            color: '#fff',
          }}
        >
          {t('auth.brand.headline')}
        </h1>
        <p style={{ fontSize: 15, opacity: 0.82, lineHeight: 1.55 }}>{t('auth.brand.subhead')}</p>
        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {features.map((f) => (
            <div key={f.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                }}
              >
                {f.icon}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{f.title}</div>
                <div style={{ fontSize: 12, opacity: 0.78, lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        {stats.map((s) => (
          <div
            key={s.l}
            style={{
              borderLeft: '1px solid rgba(255,255,255,0.25)',
              paddingLeft: 16,
            }}
          >
            <div
              className="num"
              style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}
            >
              {s.v}
            </div>
            <div
              style={{
                fontSize: 11,
                opacity: 0.75,
                marginTop: 2,
                maxWidth: 160,
                lineHeight: 1.35,
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>

      <footer
        style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 12,
          opacity: 0.65,
        }}
      >
        <div>© 2026 SkillScape</div>
        <div style={{ display: 'flex', gap: 18 }}>
          <a href="/docs" style={{ color: 'inherit' }}>
            {t('auth.brand.docs')}
          </a>
          <a href="/policy" style={{ color: 'inherit' }}>
            {t('auth.brand.policy')}
          </a>
          <a href="mailto:verzhitskiy_kg@cubit.su" style={{ color: 'inherit' }}>
            {t('auth.brand.support')}
          </a>
        </div>
      </footer>
    </div>
  );
};

// ── form helpers ─────────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  icon: React.ReactNode;
  autoFocus?: boolean;
  type?: string;
  disabled?: boolean;
}
const Field: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  icon,
  autoFocus,
  type = 'text',
  disabled,
}) => {
  const [focus, setFocus] = useState(false);
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--ink-2)',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          height: 44,
          background: 'var(--surface)',
          border: '1px solid ' + (focus ? 'var(--accent)' : 'var(--line-strong)'),
          borderRadius: 'var(--radius)',
          boxShadow: focus ? '0 0 0 3px var(--accent-soft)' : 'none',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span
          style={{
            color: focus ? 'var(--accent-deep)' : 'var(--muted)',
            display: 'flex',
          }}
        >
          {icon}
        </span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--ink)',
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        />
      </div>
    </div>
  );
};

const ModeTabs: React.FC<{
  mode: 'login' | 'register';
  onChange: (v: 'login' | 'register') => void;
}> = ({ mode, onChange }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--line)',
      }}
    >
      {[
        { v: 'login' as const, label: t('auth.loginTab'), sub: t('auth.loginTabSub') },
        {
          v: 'register' as const,
          label: t('auth.registerTab'),
          sub: t('auth.registerTabSub'),
        },
      ].map((tab) => {
        const active = tab.v === mode;
        return (
          <button
            key={tab.v}
            onClick={() => onChange(tab.v)}
            style={{
              flex: 1,
              padding: '10px 14px',
              border: 'none',
              borderRadius: 6,
              background: active ? 'var(--surface)' : 'transparent',
              color: active ? 'var(--ink)' : 'var(--muted)',
              cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              textAlign: 'left',
              transition: 'background 120ms ease',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>{tab.label}</div>
            <div
              style={{
                fontSize: 11,
                color: active ? 'var(--muted)' : 'var(--soft)',
              }}
            >
              {tab.sub}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// Песочница посетителя демо: вернуться в неё же при следующем входе (сменить роль).
const DEMO_SANDBOX_KEY = 'demoSandbox';
// Вымышленная компания песочниц (`backend/src/services/demo/seedDemo.ts`).
const DEMO_COMPANY = 'Альфа Софт';
const DEMO_ROLES = ['hr', 'manager', 'employee'] as const;
type DemoRole = (typeof DEMO_ROLES)[number];

const DemoRoleButton: React.FC<{
  title: string;
  hint?: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ title, hint, loading, disabled, onClick }) => (
  <button type="button" className="demo-role" disabled={disabled} onClick={onClick}>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{title}</span>
      {hint && <span style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.4 }}>{hint}</span>}
    </span>
    <span style={{ color: 'var(--accent)', flexShrink: 0 }}>
      {loading ? <Spinner /> : <ArrowIcon />}
    </span>
  </button>
);

const DividerWith: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--soft)' }}>
    <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    <span
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        fontWeight: 500,
      }}
    >
      {text}
    </span>
    <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
  </div>
);

const ErrorAlert: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 12px',
      background: 'var(--danger-soft)',
      border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)',
      color: 'var(--danger)',
      borderRadius: 'var(--radius)',
      fontSize: 13,
    }}
  >
    <AlertIcon />
    <span>{text}</span>
  </div>
);

const PrimaryButton: React.FC<{
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: React.ReactNode;
}> = ({ loading, disabled, onClick, type = 'submit', children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type={type}
      disabled={loading || disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        marginTop: 8,
        height: 46,
        borderRadius: 'var(--radius)',
        border: 'none',
        background: hover && !loading && !disabled ? 'var(--accent-deep)' : 'var(--accent)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        cursor: loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transition: 'background 120ms ease',
      }}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
};

// ── main page ────────────────────────────────────────────────────────────────
export const Auth: React.FC<{ isDemoEntry?: boolean }> = ({ isDemoEntry = false }) => {
  const { t, i18n } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [step, setStep] = useState<'input' | 'code' | 'company'>('input');
  // Почта в нескольких компаниях: после кода — выбор (`POST /auth/choose` по билету).
  const [choice, setChoice] = useState<{ companies: CompanyChoice[]; ticket: string } | null>(null);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  // Регистрация на skillscape.ru: домена почты нет ни у одной компании — ведём на заявку.
  const [isCompanyMissing, setIsCompanyMissing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmittedRef = useRef(false);

  const navigate = useNavigate();
  const setUser = useUserStore((s) => s.setUser);
  const setToken = useUserStore((s) => s.setToken);
  const { track } = useAnalytics();

  const { branding } = useTenant();
  // `/demo` на любом домене или страница входа демо-домена.
  const isDemo = isDemoEntry || branding.isDemo === true;
  const isSignedInElsewhere = useUserStore(
    (s) => !!s.token && !s.user?.email?.endsWith('@demo.skillscape.ru'),
  );
  const [demoRole, setDemoRole] = useState<DemoRole | null>(null);

  const registerMutation = useRegister();
  const loginMutation = useLogin();
  const verifyMutation = useVerifyCode();
  const resendMutation = useResendCode();
  const chooseMutation = useChooseCompany();

  const handleSubmit = () => {
    setError(null);
    setIsCompanyMissing(false);
    if (mode === 'register' && !name.trim()) {
      setError(t('auth.fillAllFields'));
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError(t('auth.enterEmail'));
      return;
    }
    if (mode === 'register') {
      registerMutation.mutate(
        { email, name },
        {
          onSuccess: () => setStep('code'),
          onError: (err) => {
            const isMissing =
              err instanceof ApiError &&
              err.status === 403 &&
              (err.response.data as { code?: string } | undefined)?.code === 'COMPANY_NOT_FOUND';
            setIsCompanyMissing(isMissing);
            setError(
              isMissing
                ? t('auth.companyNotFound')
                : toErrorText(err, t('errors.networkError'), t('auth.registrationError')),
            );
          },
        },
      );
    } else {
      loginMutation.mutate(
        { email },
        {
          onSuccess: () => setStep('code'),
          onError: (err) =>
            setError(toErrorText(err, t('errors.networkError'), t('auth.loginError'))),
        },
      );
    }
  };

  const signedIn = useCallback(
    (data: { token: string; user: Parameters<typeof setUser>[0] }) => {
      setToken(data.token);
      setUser(data.user);
      track(AnalyticsEvents.USER_LOGGED_IN, { method: 'email-code' });
      navigate('/');
    },
    [setToken, setUser, track, navigate],
  );

  const handleChooseCompany = (tenantId: string) => {
    if (!choice) return;
    setError(null);
    setChosenId(tenantId);
    chooseMutation.mutate(
      { ticket: choice.ticket, tenantId },
      {
        onSuccess: signedIn,
        onError: (err) => {
          setChosenId(null);
          // Билет живёт 10 минут: истёк — назад к почте, за новым кодом.
          setStep('input');
          setChoice(null);
          setCode(['', '', '', '', '', '']);
          setError(toErrorText(err, t('errors.networkError'), t('auth.chooseCompanyExpired')));
        },
      },
    );
  };

  const handleVerifyCode = useCallback(
    (submittedCode?: string) => {
      const c = submittedCode ?? code.join('');
      if (c.length !== 6) {
        setError(t('auth.enterSixDigitCode'));
        return;
      }
      if (isVerifying) return;
      setError(null);
      setIsVerifying(true);
      verifyMutation.mutate(
        { email, code: c },
        {
          onSuccess: (data) => {
            if (data.companies) {
              setChoice({ companies: data.companies, ticket: data.ticket });
              setStep('company');
              return;
            }
            signedIn(data);
          },
          onError: (err) => {
            setError(toErrorText(err, t('errors.networkError'), t('auth.verificationError')));
            setIsVerifying(false);
            // Неверный код стираем: иначе заново отправить можно, только удалив цифру.
            setCode(['', '', '', '', '', '']);
          },
          onSettled: () => setIsVerifying(false),
        },
      );
    },
    [code, email, verifyMutation, signedIn, isVerifying, t],
  );

  // Кнопка только для `npm start`: запрашивает код обычным путём и подставляет
  // его из локальной базы через dev-сервер (src/setupProxy.js). В продовой сборке
  // условие NODE_ENV вырезает и кнопку, и этот вызов.
  const handleDevLogin = async () => {
    if (!email.trim()) {
      setError(t('auth.enterEmail'));
      return;
    }
    setError(null);
    try {
      await loginMutation.mutateAsync({ email });
      const res = await fetch(`/__dev/login-code?email=${encodeURIComponent(email.trim())}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.message);
      handleVerifyCode(body.code);
    } catch (err) {
      setError(toErrorText(err, t('errors.networkError'), t('auth.devLoginFailed')));
    }
  };

  const handleDemoLogin = async (role: DemoRole) => {
    setError(null);
    setDemoRole(role);
    let sandbox: string | null = null;
    try {
      sandbox = localStorage.getItem(DEMO_SANDBOX_KEY);
    } catch {
      /* без памяти — новая песочница */
    }
    try {
      const data = await api('/auth/demo-login', { method: 'POST', body: { role, sandbox } });
      try {
        localStorage.setItem(DEMO_SANDBOX_KEY, data.sandbox);
      } catch {
        /* следующий вход создаст новую песочницу */
      }
      setToken(data.token);
      setUser(data.user);
      track(AnalyticsEvents.USER_LOGGED_IN, { method: 'demo', role });
      navigate('/');
    } catch (err) {
      setError(toErrorText(err, t('errors.networkError'), t('auth.demo.failed')));
      setDemoRole(null);
    }
  };

  // Auto-submit when 6 digits filled
  useEffect(() => {
    const full = code.every((c) => c !== '');
    if (full && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      handleVerifyCode(code.join(''));
    }
    if (!full) {
      autoSubmittedRef.current = false;
    }
  }, [code, handleVerifyCode]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    if (step === 'code') codeRefs.current[0]?.focus();
  }, [step]);

  const handleResendCode = () => {
    setError(null);
    setResendIn(45);
    resendMutation.mutate(
      { email },
      {
        onSuccess: () =>
          showToast({
            title: t('auth.codeSentTitle'),
            message: t('auth.codeSentMessage'),
            type: 'success',
          }),
        onError: (err) =>
          setError(toErrorText(err, t('errors.networkError'), t('auth.resendError'))),
      },
    );
  };

  const handleBackToInput = () => {
    setStep('input');
    setCode(['', '', '', '', '', '']);
    setError(null);
    setIsVerifying(false);
  };

  const handleModeChange = (v: 'login' | 'register') => {
    setMode(v);
    setStep('input');
    setCode(['', '', '', '', '', '']);
    setError(null);
    setName('');
  };

  const handleCodeChange = (idx: number, v: string) => {
    const digits = v.replace(/\D/g, '');
    // Multi-digit value — OS one-time-code autofill drops the whole code into
    // a single box. Spread it across all six, same as a paste.
    if (digits.length > 1) {
      const spread = Array.from({ length: 6 }, (_, i) => digits[i] || '');
      setCode(spread);
      codeRefs.current[Math.min(digits.length, 5)]?.focus();
      return;
    }
    const next = [...code];
    next[idx] = digits;
    setCode(next);
    if (digits && idx < 5) codeRefs.current[idx + 1]?.focus();
  };
  const handleCodeKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
  };
  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length >= 1) {
      e.preventDefault();
      const next: string[] = [];
      for (let i = 0; i < 6; i++) next.push(text[i] || '');
      setCode(next);
      codeRefs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  const inputLoading = registerMutation.isPending || loginMutation.isPending;
  const verifyLoading = verifyMutation.isPending || isVerifying;
  // После неудачной проверки поля пустые и снова доступны — ставим курсор в первое.
  // Раньше нельзя: пока идёт проверка, поля выключены и фокус не встаёт.
  const isCodeEmpty = code.every((c) => c === '');
  useEffect(() => {
    if (!verifyLoading && isCodeEmpty && error) codeRefs.current[0]?.focus();
  }, [verifyLoading, isCodeEmpty, error]);

  const policyAction =
    step === 'code'
      ? t('auth.policyAgreeAction.code')
      : mode === 'register'
        ? t('auth.policyAgreeAction.registerInput')
        : t('auth.policyAgreeAction.loginInput');

  return (
    <div className={'auth-root ' + (isDark ? 'dark' : '')}>
      <style>{SCOPED_STYLES}</style>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
        {/* На `/demo` компания домена ни при чём: это вход в демо SkillScape. */}
        <BrandPanel dark={isDark} name={isDemoEntry ? 'SkillScape' : undefined} />

        <div
          className="form-panel"
          style={{
            flex: '1 1 50%',
            minWidth: 420,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            background: 'var(--bg)',
          }}
        >
          <div style={{ width: '100%', maxWidth: 440 }}>
            {step === 'input' && isDemo && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <h2
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                      marginBottom: 8,
                    }}
                  >
                    {t('auth.demo.title')}
                  </h2>
                  <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>
                    {t('auth.demo.subtitle', { company: DEMO_COMPANY })}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {DEMO_ROLES.map((role) => (
                    <DemoRoleButton
                      key={role}
                      title={t(`auth.demo.roles.${role}.title`)}
                      hint={t(`auth.demo.roles.${role}.hint`)}
                      loading={demoRole === role}
                      disabled={demoRole !== null}
                      onClick={() => handleDemoLogin(role)}
                    />
                  ))}
                </div>
                {error && <ErrorAlert text={error} />}
                {isSignedInElsewhere && (
                  <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                    {t('auth.demo.signedInWarning')}
                  </p>
                )}
                <p style={{ fontSize: 12, color: 'var(--soft)', lineHeight: 1.5 }}>
                  {t('auth.demo.note')}
                </p>
              </div>
            )}

            {step === 'input' && !isDemo && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {mode === 'login' ? t('auth.welcomeBack') : t('auth.newAccount')}
                  </div>
                  <h2
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                      marginBottom: 8,
                    }}
                  >
                    {mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
                  </h2>
                  <p style={{ fontSize: 14, color: 'var(--muted)' }}>
                    {mode === 'login' ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
                  </p>
                </div>

                <ModeTabs mode={mode} onChange={handleModeChange} />

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  {mode === 'register' && (
                    <Field
                      label={t('auth.nameLabel')}
                      value={name}
                      onChange={setName}
                      placeholder={t('auth.namePlaceholder')}
                      icon={<UserIcon />}
                      autoFocus
                      disabled={inputLoading}
                    />
                  )}
                  <Field
                    label={t('auth.email')}
                    value={email}
                    onChange={setEmail}
                    placeholder="you@gmail.com"
                    icon={<MailIcon />}
                    autoFocus={mode === 'login'}
                    type="email"
                    disabled={inputLoading}
                  />

                  {error && <ErrorAlert text={error} />}
                  {error && isCompanyMissing && (
                    <a href="/#start" style={{ fontSize: 13, color: 'var(--accent-deep)' }}>
                      {t('auth.companyNotFoundLink')}
                    </a>
                  )}

                  <PrimaryButton loading={inputLoading}>
                    <MailIcon /> {t('auth.getCodeFull')} <ArrowIcon />
                  </PrimaryButton>
                </form>

                {process.env.NODE_ENV === 'development' && <DividerWith text={t('auth.or')} />}
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={handleDevLogin}
                    disabled={inputLoading || isVerifying}
                    style={{
                      height: 44,
                      borderRadius: 'var(--radius)',
                      border: '1px dashed var(--line-strong)',
                      background: 'transparent',
                      color: 'var(--ink)',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {t('auth.devLogin')}
                  </button>
                )}
              </div>
            )}

            {step === 'company' && choice && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <h2
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                      marginBottom: 8,
                    }}
                  >
                    {t('auth.chooseCompanyTitle')}
                  </h2>
                  <p style={{ fontSize: 14, color: 'var(--muted)' }}>
                    {t('auth.chooseCompanySubtitle', { email })}
                  </p>
                </div>
                {choice.companies.map((c) => (
                  <DemoRoleButton
                    key={c.id}
                    title={c.name}
                    loading={chosenId === c.id}
                    disabled={chooseMutation.isPending}
                    onClick={() => handleChooseCompany(c.id)}
                  />
                ))}
                {error && <ErrorAlert text={error} />}
              </div>
            )}

            {step === 'code' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {t('auth.verifyEyebrow')}
                  </div>
                  <h2
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                      marginBottom: 8,
                    }}
                  >
                    {t('auth.verifyTitle')}
                  </h2>
                  <p style={{ fontSize: 14, color: 'var(--muted)' }}>
                    {t('auth.verifySubtitlePrefix')}
                    <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{email}</strong>
                    {t('auth.verifySubtitleSuffix')}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  {code.map((v, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        codeRefs.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      aria-label={t('auth.codeDigit', { n: i + 1 })}
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      maxLength={i === 0 ? 6 : 1}
                      value={v}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKey(i, e)}
                      onPaste={i === 0 ? handleCodePaste : undefined}
                      disabled={verifyLoading}
                      className="num"
                      style={{
                        width: 54,
                        height: 60,
                        fontSize: 26,
                        fontWeight: 600,
                        textAlign: 'center',
                        borderRadius: 'var(--radius)',
                        border: '1px solid ' + (v ? 'var(--accent)' : 'var(--line-strong)'),
                        background: v ? 'var(--accent-soft)' : 'var(--surface)',
                        color: v ? 'var(--accent-deep)' : 'var(--ink)',
                        outline: 'none',
                        transition: 'background 120ms, border-color 120ms',
                        fontFamily: 'inherit',
                      }}
                    />
                  ))}
                </div>

                {verifyLoading && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 13,
                      color: 'var(--accent-deep)',
                    }}
                  >
                    <Spinner color="var(--accent-deep)" /> {t('auth.checkingCode')}
                  </div>
                )}

                {error && <ErrorAlert text={error} />}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 13,
                  }}
                >
                  <button
                    onClick={handleBackToInput}
                    disabled={verifyLoading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--muted)',
                      cursor: 'pointer',
                      fontSize: 13,
                      padding: 0,
                    }}
                  >
                    <BackIcon /> {t('auth.differentEmail')}
                  </button>
                  <button
                    onClick={handleResendCode}
                    disabled={resendIn > 0 || resendMutation.isPending}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: resendIn > 0 ? 'var(--muted)' : 'var(--accent-deep)',
                      fontWeight: 500,
                      cursor: resendIn > 0 ? 'default' : 'pointer',
                      fontSize: 13,
                      padding: 0,
                    }}
                  >
                    {resendIn > 0 ? t('auth.resendInSec', { n: resendIn }) : t('auth.resendCode')}
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: 32,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                color: 'var(--muted)',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              {/* На выборе компании согласие уже дано кнопкой «Получить код». */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {step !== 'company' && (
                  <>
                    {isDemo
                      ? t('auth.demo.policyPrefix')
                      : `${t('auth.policyAgreePrefix')}${policyAction}${t('auth.policyAgreeSuffix')}`}
                    <a href="/policy" style={{ color: 'var(--accent-deep)', fontWeight: 500 }}>
                      {t('auth.policyAgreementLink')}
                    </a>
                    .
                  </>
                )}
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  gap: 4,
                  background: 'var(--surface-2)',
                  borderRadius: 6,
                  padding: 2,
                  border: '1px solid var(--line)',
                }}
              >
                {(['ru', 'en'] as const).map((lang) => {
                  const active = i18n.language?.startsWith(lang);
                  return (
                    <button
                      key={lang}
                      onClick={() => i18n.changeLanguage(lang)}
                      style={{
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: 4,
                        background: active ? 'var(--surface)' : 'transparent',
                        color: active ? 'var(--ink)' : 'var(--muted)',
                        cursor: 'pointer',
                      }}
                    >
                      {lang.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
